import type { ReactNode } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  formatWorkflowDeadline,
  isWorkflowOverdue,
  workflowDeadlineSummary,
  workflowDeadlineTone,
} from "@/lib/deadlineDisplay";
import { cn } from "@/lib/utils";
import { InlineEditableTitle } from "@/components/workflow_tracking/InlineEditableTitle";

export type WorkflowTask = {
  id: number;
  title: string;
  due_at: string | null;
  completed_at: string | null;
  assignee_id?: number | null;
  assignee_name?: string | null;
  kind?: string | null;
};

type AssigneeSelectConfig = {
  collaborators: Array<{ id: number; email: string }>;
  currentUserId?: number | null;
  loading?: boolean;
  disabled?: boolean;
};

type KindSelectConfig = {
  options: Array<{ value: string; label: string }>;
  defaultValue: string;
  ariaLabel?: string;
};

function assigneeSelectOptions(
  task: WorkflowTask,
  collaborators: Array<{ id: number; email: string }>,
): Array<{ id: number; email: string }> {
  if (task.assignee_id == null) {
    return collaborators;
  }
  if (collaborators.some((collaborator) => collaborator.id === task.assignee_id)) {
    return collaborators;
  }
  return [
    ...collaborators,
    {
      id: task.assignee_id,
      email: task.assignee_name ?? `User #${task.assignee_id}`,
    },
  ];
}

type WorkflowTaskChecklistProps = {
  testId: string;
  heading: string;
  description: string;
  tasksQueryKey: readonly unknown[];
  eventsQueryKey?: readonly unknown[];
  listTasks: () => Promise<WorkflowTask[]>;
  createTask: (payload: { title: string; due_at: string | null; kind?: string }) => Promise<unknown>;
  updateTask: (
    taskId: number,
    patch: {
      completed_at?: string | null;
      title?: string;
      due_at?: string | null;
      assignee_id?: number | null;
    },
  ) => Promise<unknown>;
  deleteTask: (taskId: number) => Promise<unknown>;
  onInvalidatePipeline: (queryClient: ReturnType<typeof useQueryClient>) => void;
  emptyMessage?: string;
  loadingMessage?: string;
  newItemPlaceholder?: string;
  addButtonLabel?: string;
  dueDatePrefix?: string;
  createFormVariant?: "inline" | "boxed";
  footer?: ReactNode;
  assigneeSelect?: AssigneeSelectConfig;
  kindSelect?: KindSelectConfig;
  renderTaskBadge?: (task: WorkflowTask) => ReactNode;
  showTaskStatus?: boolean;
  dueDateMode?: "plain" | "summary";
  headerMeta?: ReactNode;
  /** Opt-in: let users rename a task title in place. */
  allowRenameTask?: boolean;
  /** Optional content rendered under each task row (e.g. a notes disclosure). */
  renderTaskFooter?: (task: WorkflowTask) => ReactNode;
  /** View-only: no create form, no delete, no rename, checkbox locked (ENG-1877). */
  readOnly?: boolean;
};

export function WorkflowTaskChecklist({
  testId,
  heading,
  description,
  tasksQueryKey,
  eventsQueryKey,
  listTasks,
  createTask,
  updateTask,
  deleteTask,
  onInvalidatePipeline,
  emptyMessage = "No tasks yet. Add the first step below.",
  loadingMessage = "Loading tasks…",
  newItemPlaceholder = "New task",
  addButtonLabel = "Add",
  dueDatePrefix = "",
  createFormVariant = "inline",
  footer,
  assigneeSelect,
  kindSelect,
  renderTaskBadge,
  showTaskStatus = false,
  dueDateMode = "plain",
  headerMeta,
  allowRenameTask = false,
  renderTaskFooter,
  readOnly = false,
}: WorkflowTaskChecklistProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  // Empty string means "no explicit choice"; fall back to the configured default
  // so the picker works even when kindSelect appears after mount.
  const [kindChoice, setKindChoice] = useState("");
  const kind = kindChoice || (kindSelect?.defaultValue ?? "");
  const [showMineOnly, setShowMineOnly] = useState(false);

  const { data: tasks = [], isLoading, error } = useQuery({
    queryKey: tasksQueryKey,
    queryFn: listTasks,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: tasksQueryKey });
    if (eventsQueryKey) {
      queryClient.invalidateQueries({ queryKey: eventsQueryKey });
    }
    onInvalidatePipeline(queryClient);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createTask({
        title: title.trim(),
        due_at: dueDate ? new Date(dueDate).toISOString() : null,
        ...(kindSelect ? { kind } : {}),
      }),
    onSuccess: () => {
      setTitle("");
      setDueDate("");
      setKindChoice("");
      invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      taskId,
      patch,
    }: {
      taskId: number;
      patch: { completed_at?: string | null };
    }) => updateTask(taskId, patch),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (taskId: number) => deleteTask(taskId),
    onSuccess: invalidate,
  });

  const assigneeMutation = useMutation({
    mutationFn: ({
      taskId,
      assigneeId,
    }: {
      taskId: number;
      assigneeId: number | null;
    }) => updateTask(taskId, { assignee_id: assigneeId }),
    onSuccess: invalidate,
  });

  const completedCount = tasks.filter((task) => task.completed_at).length;
  const UNASSIGNED = "unassigned";

  const currentUserId = assigneeSelect?.currentUserId ?? null;
  const mineCount = currentUserId
    ? tasks.filter((task) => task.assignee_id === currentUserId).length
    : 0;
  const visibleTasks =
    showMineOnly && currentUserId
      ? tasks.filter((task) => task.assignee_id === currentUserId)
      : tasks;

  const createForm = (
    <div
      className={cn(
        createFormVariant === "boxed" && "space-y-2 rounded-lg border bg-muted/10 p-3",
        createFormVariant === "inline" && "flex flex-col gap-2 sm:flex-row sm:items-end",
      )}
    >
      <div
        className={cn(
          createFormVariant === "inline" && "min-w-0 flex-1 space-y-1",
          createFormVariant === "boxed" && "flex flex-wrap gap-2",
        )}
      >
        <Label htmlFor={`${testId}-new-task`} className="sr-only">
          {newItemPlaceholder}
        </Label>
        <Input
          id={`${testId}-new-task`}
          className={cn(createFormVariant === "boxed" && "min-w-[10rem] grow-[2] basis-56")}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={newItemPlaceholder}
          aria-label={newItemPlaceholder}
        />
        <Input
          id={`${testId}-task-due`}
          type="date"
          className={
            createFormVariant === "inline" ? "w-full sm:w-[9.5rem]" : "min-w-[8.5rem] grow basis-40"
          }
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          aria-label="Due date"
        />
      </div>
      {kindSelect ? (
        <>
          <Label htmlFor={`${testId}-task-kind`} className="sr-only">
            {kindSelect.ariaLabel ?? "Type"}
          </Label>
          <Select value={kind} onValueChange={setKindChoice}>
            <SelectTrigger
              id={`${testId}-task-kind`}
              className={createFormVariant === "inline" ? "w-full sm:w-[9.5rem]" : "w-full"}
              aria-label={kindSelect.ariaLabel ?? "Type"}
            >
              {/* Radix only learns item labels once the options mount, so render it directly. */}
              <SelectValue>
                {kindSelect.options.find((option) => option.value === kind)?.label}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {kindSelect.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      ) : null}
      <Button
        type="button"
        size={createFormVariant === "boxed" ? "sm" : "default"}
        disabled={!title.trim() || createMutation.isPending}
        onClick={() => createMutation.mutate()}
      >
        <Plus className={cn("mr-2", createFormVariant === "boxed" ? "h-3.5 w-3.5" : "h-4 w-4")} />
        {createMutation.isPending && createFormVariant === "boxed" ? "Adding…" : addButtonLabel}
      </Button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid={testId}>
      {/* Every suite renders this checklist in a half-width detail rail, so
          around laptop widths the grant version's shrink-0 cluster took ~198px
          of a ~230px card: the heading clipped behind the filter button and the
          description wrapped one word per line (ENG-1984). Wrapping drops the
          cluster to its own line first. The basis has to be small: it sets the
          break point at basis + gap + cluster, and the permit and trust suites
          pass no assigneeSelect, so their cluster is a bare "N/M done" (~50px)
          in a card that reaches ~157px. basis-24 puts their threshold at 154px
          and keeps them on one line, while the grant cluster (~198px, filter
          button included) still breaks well above its ~230px card. */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 grow basis-24 space-y-1">
          <h3 className="text-base font-semibold">{heading}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
          {headerMeta}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {currentUserId && tasks.length > 0 ? (
            <Button
              type="button"
              variant={showMineOnly ? "secondary" : "outline"}
              size="sm"
              className="h-7 text-xs"
              aria-pressed={showMineOnly}
              onClick={() => setShowMineOnly((value) => !value)}
            >
              Assigned to me ({mineCount})
            </Button>
          ) : null}
          {tasks.length > 0 ? (
            <span className="text-xs text-muted-foreground tabular-nums">
              {completedCount}/{tasks.length} done
            </span>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {loadingMessage}
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">Could not load tasks.</p>
      ) : tasks.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-5 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : visibleTasks.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-5 text-center text-sm text-muted-foreground">
          Nothing is assigned to you yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {visibleTasks.map((task) => {
            const completed = Boolean(task.completed_at);
            const overdue = !completed && Boolean(task.due_at) && isWorkflowOverdue(task.due_at);
            const dueTone =
              !completed && task.due_at ? workflowDeadlineTone(task.due_at) : "muted";
            const dueSummary =
              dueDateMode === "summary"
                ? workflowDeadlineSummary(task.due_at, { emptyLabel: "No due date" })
                : task.due_at
                  ? `${dueDatePrefix}${formatWorkflowDeadline(task.due_at)}`
                  : null;

            return (
              <li
                key={task.id}
                className={cn(
                  "rounded-lg border bg-background px-3 py-2.5",
                  completed && "bg-muted/20",
                  overdue &&
                    "border-amber-300/80 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20",
                )}
              >
                <div className="flex items-start gap-3">
                <Checkbox
                  checked={completed}
                  disabled={readOnly || updateMutation.isPending}
                  onCheckedChange={(checked) =>
                    updateMutation.mutate({
                      taskId: task.id,
                      patch: { completed_at: checked ? new Date().toISOString() : null },
                    })
                  }
                  aria-label={`Mark ${task.title} complete`}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {allowRenameTask && !readOnly ? (
                      <InlineEditableTitle
                        value={task.title}
                        onSubmit={(next) =>
                          updateMutation.mutate({ taskId: task.id, patch: { title: next } })
                        }
                        inputAriaLabel="Edit task title"
                        editButtonAriaLabel={`Rename ${task.title}`}
                        className={cn(
                          "text-sm font-medium",
                          completed && "line-through text-muted-foreground",
                        )}
                      />
                    ) : (
                      <p
                        className={cn(
                          "min-w-0 break-words text-sm font-medium",
                          completed && "line-through text-muted-foreground",
                        )}
                      >
                        {task.title}
                      </p>
                    )}
                    {showTaskStatus ? (
                      <Badge
                        variant={completed ? "secondary" : "outline"}
                        className={cn(
                          "font-normal",
                          overdue &&
                            "border-amber-300 text-amber-800 dark:border-amber-800 dark:text-amber-200",
                        )}
                      >
                        {completed ? "Completed" : "Open"}
                      </Badge>
                    ) : null}
                    {renderTaskBadge?.(task)}
                    {currentUserId && task.assignee_id === currentUserId ? (
                      <Badge variant="secondary" className="text-[10px]">
                        You
                      </Badge>
                    ) : null}
                  </div>
                  {dueSummary ? (
                    <p
                      className={cn(
                        "text-xs",
                        !completed && dueTone === "warning"
                          ? "text-amber-700 dark:text-amber-400"
                          : "text-muted-foreground",
                      )}
                    >
                      {dueSummary}
                    </p>
                  ) : null}
                  {assigneeSelect ? (
                    <div className="mt-2 max-w-xs">
                      <Label htmlFor={`${testId}-assignee-${task.id}`} className="sr-only">
                        Assignee for {task.title}
                      </Label>
                      <Select
                        value={task.assignee_id ? String(task.assignee_id) : UNASSIGNED}
                        onValueChange={(value) =>
                          assigneeMutation.mutate({
                            taskId: task.id,
                            assigneeId: value === UNASSIGNED ? null : Number(value),
                          })
                        }
                        disabled={
                          assigneeSelect.disabled ||
                          assigneeSelect.loading ||
                          (assigneeMutation.isPending &&
                            assigneeMutation.variables?.taskId === task.id)
                        }
                      >
                        <SelectTrigger
                          id={`${testId}-assignee-${task.id}`}
                          className="h-8 text-xs"
                          aria-label={`Assignee for ${task.title}`}
                        >
                          <SelectValue
                            placeholder={
                              assigneeSelect.loading ? "Loading team…" : "Unassigned"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                          {assigneeSelectOptions(task, assigneeSelect.collaborators).map(
                            (collaborator) => (
                              <SelectItem key={collaborator.id} value={String(collaborator.id)}>
                                {collaborator.email}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : task.assignee_name ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Assigned to {task.assignee_name}
                    </p>
                  ) : null}
                </div>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => deleteMutation.mutate(task.id)}
                    aria-label={`Delete ${task.title}`}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                </div>
                {renderTaskFooter ? (
                  <div className="mt-2 border-t pt-2 pl-8">{renderTaskFooter(task)}</div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && createForm}
      {footer}
    </div>
  );
}
